import { computed, getCurrentScope, onScopeDispose, watch, type ComputedRef, type Ref } from 'vue'

import { platformActionApi } from '../api/PlatformActionApi'
import { LoadActionDetailFlow } from '../flows/LoadActionDetailFlow'
import type { PlatformActionDetail } from '../models/platformAction.types'
import type { PlatformActionError } from '../models/platformActionError'
import { isTerminalStatus } from '../service/actionStatus'
import { usePlatformActionStore } from '../stores/platformActionStore'

import { useActionPolling } from './useActionPolling'

import { useWorkspace } from '@/features/account'
import type { UiAsyncState } from '@/shared/ui'

/**
 * There is no push channel for a change's progress, so the page re-reads itself every 10s until
 * the status is terminal — and never after.
 */
export const CHANGE_DETAIL_POLL_MS = 10_000

interface DetailFlow {
  execute: LoadActionDetailFlow['execute']
}

/** Internal injection seam: production callers use the no-argument contract. */
export interface UseChangeDetailOptions {
  flow?: DetailFlow
  intervalMs?: number
  schedule?: (callback: () => void, ms: number) => number
  cancel?: (handle: number) => void
}

export interface UseChangeDetail {
  detail: ComputedRef<PlatformActionDetail | null>
  state: ComputedRef<UiAsyncState>
  error: ComputedRef<PlatformActionError | null>
  /** Nothing moves out of a terminal status, so nothing is worth re-reading either. */
  isSettled: ComputedRef<boolean>
  reload: () => Promise<void>
}

/**
 * One change, read-only. The screen is a reader: the request moves only because the worker moved
 * it, which is why it re-reads instead of predicting the next status. Polling also stops while the
 * tab is in the background — a screen nobody is looking at has nothing to keep current.
 */
export function useChangeDetail(
  actionId: Ref<string>,
  options: UseChangeDetailOptions = {},
): UseChangeDetail {
  const workspace = useWorkspace()
  const store = usePlatformActionStore()
  const flow = options.flow ?? new LoadActionDetailFlow({ api: platformActionApi, store })

  const workspaceId = computed(() => {
    const value = workspace.workspaceId.value
    if (value === undefined || value === '') return null
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
  })

  const polling = useActionPolling(
    {
      load: async (id, action) => {
        // A failed tick leaves the last good read on screen: it is still the truest thing we have.
        const result = await flow.execute(id, action, { silent: true })
        return result?.ok === true ? result.value.request : null
      },
    },
    {
      intervalMs: options.intervalMs ?? CHANGE_DETAIL_POLL_MS,
      maxTicks: Number.POSITIVE_INFINITY,
      ...(options.schedule ? { schedule: options.schedule } : {}),
      ...(options.cancel ? { cancel: options.cancel } : {}),
    },
  )

  const isSettled = computed(() =>
    store.detail ? isTerminalStatus(store.detail.request.status) : false,
  )

  function isHidden(): boolean {
    return typeof document !== 'undefined' && document.hidden
  }

  async function load(silent: boolean): Promise<void> {
    const id = workspaceId.value
    const action = actionId.value
    polling.stop()
    if (id === null || !action) return
    const result = await flow.execute(id, action, { silent })
    if (result?.ok !== true || isTerminalStatus(result.value.request.status) || isHidden()) return
    polling.start(id, action)
  }

  function onVisibility(): void {
    if (isHidden()) polling.stop()
    else void load(true)
  }

  watch(
    () => [workspaceId.value, actionId.value] as const,
    () => {
      store.clearDetail()
      void load(false)
    },
    { immediate: true },
  )

  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility)
  if (getCurrentScope()) {
    onScopeDispose(() => {
      polling.stop()
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility)
      }
    })
  }

  return {
    detail: computed(() => store.detail),
    state: computed(() => {
      if (store.detailStatus === 'loading' || store.detailStatus === 'idle') return 'loading'
      if (store.detailStatus === 'error') return 'error'
      return store.detail ? 'ready' : 'empty'
    }),
    error: computed(() => store.detailError),
    isSettled,
    reload: () => load(false),
  }
}
